import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { PageHead } from '../components/page-head.js';
import { DomainApiError } from '../lib/api-error.js';
import { useSession, readCalendarViewPreference, writeCalendarViewPreference } from '../stores/session.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import {
  calendarFeedUrl,
  calendarIcalUrl,
  getCalendarFeedStatus,
  listCalendarEventTypes,
  listCalendarEvents,
  listDueReminders,
  revokeCalendarFeed,
  rotateCalendarFeedToken,
  sendDueReminders,
  updateCalendarEvent,
  type CalendarEvent,
  type CalendarEventType,
  type CalendarEventTypeKey,
  type CalendarReminderCandidate,
} from '../lib/calendar-api.js';
import {
  CALENDAR_VIEWS,
  calendarLegend,
  calendarRange,
  eventToneClass,
  groupByLocalDay,
  isCalendarView,
  stepAnchor,
  visibleEvents,
  type CalendarView,
} from '../lib/calendar-range.js';
import { Plus } from 'lucide-react';
import {
  CalendarWidget,
  type CalendarEventInput,
  type CalendarResourceInput,
} from '../components/calendar-widget.js';
import { CalendarEventDrawer } from '../components/calendar-event-drawer.js';
import { CalendarEventForm, type CalendarEventFormSeed } from '../components/calendar-event-form.js';
import { formatDateTime, formatTime } from '@reos/i18n';
import { listGoogleBusy, type GoogleBusyInterval } from '../lib/google-calendar-api.js';

/**
 * Kalendář makléře (krok CRM3, obrazovka `/kalendar`).
 *
 * Uzemní se na tři obrazovky, které AUD1/UX1 skryly jako prázdné:
 * `/kalendar` tady, `/prohlidky` a `/pozornost` až CRM4 (viz report).
 *
 * Vzor je Pobytník `/calendar` (den/týden/měsíc/seznam, zdroje = lidé,
 * drag & resize) a data leží v `apps/web-admin/src/lib/calendar-range.ts`,
 * aby se „týden" počítal na jednom místě (viz ROZHODNUTÍ CRM3-11).
 */

const VIEW_LABELS: Readonly<Record<CalendarView, 'webAdmin.calendar.day' | 'webAdmin.calendar.week' | 'webAdmin.calendar.month' | 'webAdmin.calendar.list'>> = {
  day: 'webAdmin.calendar.day',
  week: 'webAdmin.calendar.week',
  month: 'webAdmin.calendar.month',
  list: 'webAdmin.calendar.list',
};

/**
 * Krok FIX4 bod 2: týden se 7 sloupci po ~30 px je na mobilu nečitelný.
 * Bez uložené volby makléře (`readCalendarViewPreference`) se na úzké
 * obrazovce otevírá jiné zobrazení — stejný breakpoint jako „⋯" v horní
 * liště (`page-head.tsx` má 900 px pro cely shell, tenhle je uzsi zamerne:
 * 640 px je sirka, pod kterou uz je i TYDEN nečitelný, ne jen bocni menu).
 *
 * Krok KAL1 bod 5: na telefonu je výchozí SEZNAM (agenda po dnech), ne Den.
 * Den zůstává druhou volbou. Důvod: na 390 px se do mřížky dne vejde pár
 * hodin a makléř musí rolovat, aby zjistil, co ho dnes čeká; agenda ukáže
 * celý týden na jedné obrazovce.
 */
const MOBILE_CALENDAR_BREAKPOINT = 640;

function defaultCalendarView(): CalendarView {
  const stored = readCalendarViewPreference();
  if (stored !== null && isCalendarView(stored)) return stored;
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_CALENDAR_BREAKPOINT ? 'list' : 'week';
}

export function KalendarPage(): React.JSX.Element {
  const t = useT();
  const currentUserId = useSession((s) => s.user?.id ?? null);

  const [view, setView] = React.useState<CalendarView>(defaultCalendarView);
  const changeView = React.useCallback((next: CalendarView) => {
    setView(next);
    writeCalendarViewPreference(next);
  }, []);
  const [anchor, setAnchor] = React.useState<Date>(new Date(Date.now()));
  const [mineOnly, setMineOnly] = React.useState(false);
  const [ownerFilter, setOwnerFilter] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState<'' | CalendarEventTypeKey>('');

  const [events, setEvents] = React.useState<CalendarEvent[]>([]);
  const [types, setTypes] = React.useState<CalendarEventType[]>([]);
  const [members, setMembers] = React.useState<TenantMember[]>([]);
  const [reminders, setReminders] = React.useState<CalendarReminderCandidate[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [formSeed, setFormSeed] = React.useState<CalendarEventFormSeed | null>(null);
  const [feedUrl, setFeedUrl] = React.useState<string | null>(null);
  const [reminderSummary, setReminderSummary] = React.useState<string | null>(null);
  /** Krok GCAL1 bod 3: obsazene bloky z Google kalendare prihlaseneho maklere. */
  const [googleBusy, setGoogleBusy] = React.useState<GoogleBusyInterval[]>([]);

  const range = React.useMemo(() => calendarRange(view, anchor, 'cs-CZ'), [view, anchor]);
  const effectiveOwner = ownerFilter !== '' ? ownerFilter : mineOnly ? (currentUserId ?? '') : '';

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [nextEvents, nextTypes, nextMembers, nextReminders] = await Promise.all([
        listCalendarEvents({
          from: range.from,
          to: range.to,
          ...(effectiveOwner !== '' ? { ownerUserId: effectiveOwner } : {}),
          ...(typeFilter !== '' ? { eventType: typeFilter } : {}),
        }),
        listCalendarEventTypes(),
        listMembers(),
        listDueReminders(),
      ]);
      setEvents(nextEvents);
      setTypes(nextTypes);
      setMembers(nextMembers);
      setReminders(nextReminders);
    } catch (e: unknown) {
      setLoadError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, effectiveOwner, typeFilter, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  /*
   * Krok GCAL1 bod 3: obsazenost z Googlu se nacita SAMOSTATNE, ne v
   * `Promise.all` s udalostmi. Dotaz jde do cizi sluzby a nesmi zdrzet ani
   * shodit nacteni kalendare — kdyz Google neodpovi, klient vrati prazdno
   * (`listGoogleBusy`) a mrizka se nakresli bez bloku.
   */
  React.useEffect(() => {
    let cancelled = false;
    void listGoogleBusy(range.from, range.to).then((items) => {
      if (!cancelled) setGoogleBusy(items);
    });
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  React.useEffect(() => {
    let cancelled = false;
    void getCalendarFeedStatus()
      .then((status) => {
        // Bez kliknutí na „vygenerovat odkaz" neznáme token — vrátí se jen
        // jednou. Proto obrazovka ukáže jen „odkaz existuje / neexistuje",
        // a odkaz musí makléř vytvořit tlačítkem.
        if (!cancelled && status.active) setNotice(t('webAdmin.calendar.ical.create'));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [t]);

  const shown = React.useMemo(() => visibleEvents(events, range), [events, range]);
  const legend = React.useMemo(() => calendarLegend(types, shown), [types, shown]);

  /*
   * Krok GCAL1 bod 3: obsazene bloky z Googlu kresli mrizka jako NEHYBNE
   * karty v neutralnim tonu (`rh-ec-ev--other`, zadne nove CSS). Nazvy
   * cizich udalosti REOS nema a mit nema — v karte je jen „Obsazeno".
   * `editable: false` je podstatne: blok nepatri REOSu, takze s nim nesmi
   * jit hybat (PATCH by skoncil na 404).
   */
  const busyEvents: readonly CalendarEventInput[] = React.useMemo(
    () =>
      currentUserId === null || (effectiveOwner !== '' && effectiveOwner !== currentUserId)
        ? []
        : googleBusy.map((block, index) => ({
            id: `gcal-busy-${index}`,
            resourceId: currentUserId,
            title: t('webAdmin.calendar.googleBusy'),
            start: block.startsAt,
            end: block.endsAt,
            classNames: ['rh-ec-ev--other'],
            location: '',
            editable: false,
          })),
    [googleBusy, currentUserId, effectiveOwner, t],
  );

  const libEvents: readonly CalendarEventInput[] = React.useMemo(
    () =>
      shown.map((event) => ({
        id: event.id,
        resourceId: event.ownerUserId,
        title: event.title,
        start: event.startsAt,
        end: event.endsAt,
        /*
         * Krok KAL1: tón plochy je TŘÍDA z tokenů (`eventToneClass`), ne hex
         * ze serveru — ten patří odběru iCal, ne administraci. Zrušená
         * událost se odbarví, ne schová: „bylo to tu, teď to neplatí" je
         * informace, kterou makléř potřebuje vidět.
         */
        classNames:
          event.status === 'cancelled'
            ? ['rh-ec-ev--cancelled']
            : [eventToneClass(event.eventType)],
        location: event.location ?? '',
        editable: event.canUpdate && event.status !== 'cancelled',
      })),
    [shown],
  );

  const resources: readonly CalendarResourceInput[] = React.useMemo(
    () =>
      members.map((member) => ({
        id: member.userId,
        title: member.name,
      })),
    [members],
  );

  async function moveEvent(id: string, start: string, end: string, revert: () => void): Promise<void> {
    setActionError(null);
    try {
      const updated = await updateCalendarEvent(id, { startsAt: start, endsAt: end });
      setEvents((current) => current.map((e) => (e.id === id ? updated : e)));
    } catch (e: unknown) {
      // Posunutí musí jít zpátky — jinak by kalendář ukazoval termín, který
      // v databázi není, a makléř by o něm nevěděl.
      revert();
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.actionFailed'));
    }
  }

  async function createFeed(): Promise<void> {
    setActionError(null);
    try {
      const token = await rotateCalendarFeedToken();
      setFeedUrl(calendarFeedUrl(token));
    } catch (e: unknown) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.actionFailed'));
    }
  }

  async function revokeFeed(): Promise<void> {
    setActionError(null);
    try {
      await revokeCalendarFeed();
      setFeedUrl(null);
    } catch (e: unknown) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.actionFailed'));
    }
  }

  async function runReminders(): Promise<void> {
    setActionError(null);
    setReminderSummary(null);
    try {
      const result = await sendDueReminders();
      if (result.noMailer || result.noDirectory) {
        setReminderSummary(t('webAdmin.calendar.drawer.reminderNoMailer'));
      } else {
        setReminderSummary(t('webAdmin.calendar.reminders.sent', { count: String(result.sent) }));
      }
      await load();
    } catch (e: unknown) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.reminders.failed'));
    }
  }

  const dueReminders = reminders.filter((r) => r.dueNow);

  return (
    <div data-testid="kalendar-page">
      {/*
        Krok KAL1 bod 1: kalendar ma TUTEZ kostru jako Dnesek a Zakazky —
        nadpis, podtitulek s obdobim a JEDNA primarni akce v hlavicce
        (`PageHead`), obsah v `.rh-a2-page`. Obdobi je PODTITULEK, ne radek
        mezi tlacitky: je to odpoved na „co ted vidim", ne ovladaci prvek.
      */}
      <PageHead
        title={t('webAdmin.calendar.pageTitle')}
        subtitle={<span data-testid="calendar-range">{range.label}</span>}
        action={
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            data-testid="calendar-new"
            onClick={() => setFormSeed({})}
          >
            <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
              <Plus size={15} strokeWidth={2} />
            </span>
            {t('webAdmin.calendar.newEventShort')}
          </button>
        }
      />

      <div className="rh-a2-page rh-a2-page--wide">
      <div className="rh-a2-filters">
        <div className="rh-a2-seg" role="group" aria-label={t('webAdmin.calendar.viewAriaLabel')}>
          {CALENDAR_VIEWS.map((entry) => (
            <button
              key={entry}
              type="button"
              className={entry === view ? 'is-on' : ''}
              aria-pressed={entry === view}
              data-testid={`calendar-view-${entry}`}
              onClick={() => changeView(entry)}
            >
              {t(VIEW_LABELS[entry])}
            </button>
          ))}
        </div>

        {/* Predchozi / Dnes / Dalsi je JEDNA skupina (zadani bod 4), ne tri
            samostatna tlacitka — je to jeden ovladaci prvek: posun obdobi. */}
        <div className="rh-a2-seg" role="group" aria-label={t('webAdmin.calendar.navAriaLabel')}>
          <button
            type="button"
            data-testid="calendar-prev"
            onClick={() => setAnchor((a) => stepAnchor(view, a, -1))}
          >
            {t('webAdmin.calendar.prev')}
          </button>
          <button
            type="button"
            data-testid="calendar-today"
            onClick={() => setAnchor(new Date(Date.now()))}
          >
            {t('webAdmin.calendar.today')}
          </button>
          <button
            type="button"
            data-testid="calendar-next"
            onClick={() => setAnchor((a) => stepAnchor(view, a, 1))}
          >
            {t('webAdmin.calendar.next')}
          </button>
        </div>

        {/* „Jen moje" je PREPINAC jako stitky typu — ne zaskrtavatko bez
            stylu vedle dvou nepopsanych poli (snimek R.B. 6. 10.). */}
        <button
          type="button"
          className={mineOnly ? 'rh-a2-chip is-on' : 'rh-a2-chip'}
          aria-pressed={mineOnly}
          data-testid="calendar-mine"
          onClick={() => setMineOnly((value) => !value)}
        >
          {t('webAdmin.calendar.mineOnly')}
        </button>

        {/* Vyber maklere ma VIDITELNY popisek (`.rh-a2-field`, tytez prvky
            jako filtry u Zakazek) — bez nej to bylo bile pole bez vysvetleni. */}
        <label className="rh-a2-field">
          <span>{t('webAdmin.calendar.sources')}</span>
          <select
            data-testid="calendar-owner-filter"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            aria-label={t('webAdmin.calendar.sources')}
          >
            <option value="">{t('webAdmin.calendar.sourcesAll')}</option>
            {members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {notice !== null && (
        <p role="status" className="rh-a2-muted" data-testid="calendar-notice">
          {notice}
        </p>
      )}
      {loadError !== null && (
        <p role="alert" className="rh-a2-error" data-testid="calendar-load-error">
          {loadError}
        </p>
      )}
      {actionError !== null && (
        <p role="alert" className="rh-a2-error" data-testid="calendar-action-error">
          {actionError}
        </p>
      )}
      {loading && (
        <p className="rh-a2-muted" data-testid="calendar-loading">
          {t('webAdmin.calendar.loading')}
        </p>
      )}

      {/* Stitky typu jsou PREPINACE FILTRU (zadani bod 4), ne popisky:
          klik zapne jeden typ, druhy klik ho vypne. Barva je tecka z tokenu
          `--rh-ev-*` — plna tonovana plocha by v tmavem rezimu svitila. */}
      <ul className="rh-a2-filters rh-ec-legend" data-testid="calendar-legend">
        {legend.map((entry) => (
          <li key={entry.key}>
            <button
              type="button"
              className={
                `rh-a2-chip rh-ec-chip ${eventToneClass(entry.key)}` +
                (typeFilter === entry.key ? ' is-on' : '')
              }
              aria-pressed={typeFilter === entry.key}
              data-testid={`calendar-legend-${entry.key}`}
              onClick={() => setTypeFilter((current) => (current === entry.key ? '' : entry.key))}
            >
              <span className="rh-ec-chip__dot" aria-hidden="true" />
              {entry.label}
              <span className="rh-a2-chip__n">{entry.count}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* Zadani bod 6: prazdne obdobi nabidne zalozeni, a to NAD mrizkou —
          veta pod kalendarem byla ~600 px od mista, kam se makler diva. */}
      {shown.length === 0 && !loading && loadError === null && (
        <div className="rh-a2-card rh-ec-empty" data-testid="calendar-empty">
          <strong>{t('webAdmin.calendar.emptyTitle')}</strong>
          <p>{t('webAdmin.calendar.emptyDescription')}</p>
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            data-testid="calendar-empty-new"
            onClick={() => setFormSeed({})}
          >
            <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
              <Plus size={15} strokeWidth={2} />
            </span>
            {t('webAdmin.calendar.newEventShort')}
          </button>
        </div>
      )}

      {/*
        ROZHODNUTI KAL1-SEZNAM: v zobrazeni Seznam kresli agendu STRANKA, ne
        knihovna. Do kroku KAL1 bezely obe vedle sebe a kazda psala u teze
        udalosti jinou hodinu (zivé mereni 6. 10.: mrizka 12:00, tabulka
        14:00). Jedna pravda o jednom terminu — a agenda po dnech je zaroven
        vychozi zobrazeni na telefonu (bod 5).
      */}
      {view !== 'list' && (
        <>
          <p className="rh-a2-muted rh-ec-hint" data-testid="calendar-hint">
            {t('webAdmin.calendar.dragHint')}
          </p>
          {/* Krok GCAL1 bod 3: vysvetleni neutralnich bloku v mrizce. */}
          {busyEvents.length > 0 ? (
            <p className="rh-a2-muted rh-ec-hint" data-testid="calendar-google-busy-legend">
              {t('webAdmin.calendar.googleBusyLegend')}
            </p>
          ) : null}
          <CalendarWidget
            events={[...libEvents, ...busyEvents]}
            resources={resources}
            view={view}
            date={anchor}
            editable
            allDayLabel={t('webAdmin.calendar.allDay')}
            onEventClick={(id) => setOpenId(id)}
            onRangeSelect={(start, end, resourceId) =>
              setFormSeed({
                startsAt: start,
                ...(resourceId !== null ? { ownerUserId: resourceId } : {}),
              })
            }
            onEventMove={(id, start, end, revert) => void moveEvent(id, start, end, revert)}
          />
        </>
      )}

      {view === 'list' && (
        <div className="rh-a2-card rh-ec-agenda" data-testid="calendar-list">
          {groupByLocalDay(shown, 'cs-CZ').map((group) => (
            <section key={group.key} className="rh-ec-agenda__day">
              <h2 className="rh-ec-agenda__head">{group.label}</h2>
              <ul className="rh-ec-agenda__list">
                {group.items.map((event) => (
                  <li key={event.id} data-testid={`calendar-list-row-${event.id}`}>
                    <button
                      type="button"
                      className={`rh-ec-agenda__row ${
                        event.status === 'cancelled' ? 'rh-ec-ev--cancelled' : eventToneClass(event.eventType)
                      }`}
                      onClick={() => setOpenId(event.id)}
                    >
                      <span className="rh-ec-agenda__time">
                        {formatTime(new Date(event.startsAt), { locale: 'cs-CZ' })}
                      </span>
                      <span className="rh-ec-agenda__body">
                        <span className="rh-ec-agenda__title">{event.title}</span>
                        <span className="rh-ec-agenda__meta">
                          {[event.typeLabel, event.location ?? '', members.find((m) => m.userId === event.ownerUserId)?.name ?? '']
                            .filter((part) => part !== '')
                            .join(' · ')}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {formSeed !== null && (
        // Krok FIX4 bod 3: pevný panel u tlačítka, ne sekce v toku stránky —
        // ta se dřív vykreslovala až POD kalendářem a widgety iCal/připomínek
        // (~1400 px pod tlačítkem na mobilu, živé ověření 1. 10.). Stejná
        // třída `.rh-demand-drawer` jako `CalendarEventDrawer` (detail
        // události) — je to tatáž obrazovka v jiném stavu.
        <aside
          className="rh-demand-drawer"
          data-testid="calendar-form-section"
          aria-label={t('webAdmin.calendar.form.title')}
        >
          <header className="rh-demand-drawer__header">
            <h2 className="rh-kanban-card__title">{t('webAdmin.calendar.form.title')}</h2>
            <button
              type="button"
              className="rh-btn"
              onClick={() => setFormSeed(null)}
              data-testid="calendar-form-close"
            >
              {t('webAdmin.calendar.form.cancel')}
            </button>
          </header>
          <CalendarEventForm
            types={types}
            members={members}
            seed={formSeed}
            onCreated={() => {
              setFormSeed(null);
              void load();
            }}
            onCancel={() => setFormSeed(null)}
            showCancel={false}
          />
        </aside>
      )}

      {/* Krok KAL1 bod 1: odber a pripominky jsou KARTY jako sekce na jinych
          obrazovkach, ne holy `<section>` s vlastnimi velikostmi pisma. */}
      <section className="rh-a2-card" data-testid="calendar-ical">
        <div className="rh-a2-card__head">
          <h2>{t('webAdmin.calendar.ical.heading')}</h2>
        </div>
        <p className="rh-a2-muted">{t('webAdmin.calendar.ical.hint')}</p>
        {feedUrl !== null ? (
          <>
            <label className="rh-a2-field rh-ec-feed">
              <span>{t('webAdmin.calendar.ical.url')}</span>
              <input
                type="text"
                data-testid="calendar-feed-url"
                value={feedUrl}
                aria-label={t('webAdmin.calendar.ical.url')}
                readOnly
              />
            </label>
            <div className="rh-a2-filters">
              <button
                type="button"
                className="rh-a2-btn"
                data-testid="calendar-feed-copy"
                onClick={() => {
                  void navigator.clipboard?.writeText(feedUrl);
                  setNotice(t('webAdmin.calendar.ical.copied'));
                }}
              >
                {t('webAdmin.calendar.ical.copy')}
              </button>
              <button
                type="button"
                className="rh-a2-btn"
                data-testid="calendar-feed-regenerate"
                onClick={() => void createFeed()}
              >
                {t('webAdmin.calendar.ical.regenerate')}
              </button>
              <button
                type="button"
                className="rh-a2-btn"
                data-testid="calendar-feed-revoke"
                onClick={() => void revokeFeed()}
              >
                {t('webAdmin.calendar.ical.revoke')}
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className="rh-a2-btn"
            data-testid="calendar-feed-create"
            onClick={() => void createFeed()}
          >
            {t('webAdmin.calendar.ical.create')}
          </button>
        )}
        <p className="rh-ec-export">
          <a className="rh-a2-link" href={calendarIcalUrl()} data-testid="calendar-ical-export" download>
            {t('webAdmin.calendar.ical.exportLabel')}
          </a>
        </p>
      </section>

      <section className="rh-a2-card" data-testid="calendar-reminders">
        <div className="rh-a2-card__head">
          <h2>{t('webAdmin.calendar.reminders.heading')}</h2>
        </div>
        <p className="rh-a2-muted">{t('webAdmin.calendar.reminders.runHint')}</p>
        {reminderSummary !== null && (
          <p role="status" data-testid="calendar-reminder-summary">
            {reminderSummary}
          </p>
        )}
        {dueReminders.length === 0 ? (
          <p className="rh-a2-muted" data-testid="calendar-reminders-empty">
            {t('webAdmin.calendar.reminders.empty')}
          </p>
        ) : (
          <ul className="rh-ec-due" data-testid="calendar-reminders-list">
            {dueReminders.map((item) => (
              <li key={item.id} data-testid={`calendar-reminder-${item.id}`}>
                {item.title} — {formatDateTime(new Date(item.remindAt), { locale: 'cs-CZ' })}
              </li>
            ))}
          </ul>
        )}
        <div className="rh-a2-filters rh-ec-foot">
          <button
            type="button"
            className="rh-a2-btn"
            data-testid="calendar-reminders-run"
            onClick={() => void runReminders()}
          >
            {t('webAdmin.calendar.reminders.run')}
          </button>
        </div>
      </section>

      {openId !== null && (
        <CalendarEventDrawer
          eventId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
          onDeleted={() => {
            setOpenId(null);
            void load();
          }}
        />
      )}
      </div>
    </div>
  );
}
